import React, { useState } from 'react';
import { AdminOrgsPage } from './AdminOrgsPage';
import { AdminOnboardWizard } from './AdminOnboardWizard';

interface AdminShellProps {
  userEmail: string;
  userName: string;
  onExit: () => void;
  onSignOut: () => void;
  isDark: boolean;
  setIsDark: (v: boolean) => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

type AdminView = 'orgs' | 'onboard';

export function AdminShell({
  userEmail, userName, onExit, onSignOut, isDark, setIsDark, showToast,
}: AdminShellProps) {
  const [view, setView] = useState<AdminView>('orgs');
  const initials = (userName || userEmail || 'A').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex flex-col" style={{ height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      {/* Admin header — neutral slate so it visually differs from customer app */}
      <header
        style={{
          background: 'var(--admin-bg)',
          color: '#F8FAFC',
          padding: '0 var(--sp-6)',
          height: 52,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,.06)',
        }}
      >
        <div
          style={{
            width: 34, height: 34,
            background: '#F59E0B',
            borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontWeight: 700, fontSize: 14, color: '#1E293B',
          }}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l9 4v6c0 5-3.8 9.4-9 11-5.2-1.6-9-6-9-11V6l9-4z" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700, letterSpacing: '-.01em' }}>GoPal Admin</div>
          <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>Super admin console</div>
        </div>
        <span
          className="badge"
          style={{ background: '#F59E0B', color: '#1E293B', marginLeft: 8 }}
        >ADMIN</span>

        <div style={{ width: 24 }} />

        <button
          className="tab"
          onClick={() => setView('orgs')}
          style={{
            color: view === 'orgs' ? '#F8FAFC' : '#94A3B8',
            borderBottomColor: view === 'orgs' ? '#F59E0B' : 'transparent',
          }}
        >Organisations</button>
        <button
          className="tab"
          onClick={() => setView('onboard')}
          style={{
            color: view === 'onboard' ? '#F8FAFC' : '#94A3B8',
            borderBottomColor: view === 'onboard' ? '#F59E0B' : 'transparent',
          }}
        >Onboard new org</button>

        <div style={{ flex: 1 }} />

        <button
          onClick={onExit}
          style={{
            background: 'rgba(255,255,255,.06)',
            color: '#F8FAFC',
            border: '1px solid rgba(255,255,255,.12)',
            borderRadius: 'var(--r)',
            padding: '6px 14px',
            cursor: 'pointer', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
          title="Back to the customer app"
        >
          <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M10 4L6 8l4 4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          View as customer
        </button>

        <button
          className={`switch ${isDark ? 'is-on' : ''}`}
          onClick={() => setIsDark(!isDark)}
          title="Toggle dark mode"
          style={{ marginLeft: 8 }}
        />

        <button
          onClick={onSignOut}
          style={{
            width: 30, height: 30,
            borderRadius: '50%',
            background: '#F59E0B',
            color: '#1E293B',
            border: 'none',
            fontWeight: 700, fontSize: 11,
            cursor: 'pointer',
            marginLeft: 4,
          }}
          title="Sign out"
        >{initials}</button>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-6) var(--sp-8)' }}>
        {view === 'orgs'    && <AdminOrgsPage showToast={showToast} />}
        {view === 'onboard' && <AdminOnboardWizard showToast={showToast} onDone={() => setView('orgs')} />}
      </div>
    </div>
  );
}
